import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { avisarCambio } from './cambios';
import { useSesion } from './sesion';

export class ErrorApi extends Error {
  constructor(
    readonly estado: number,
    mensaje: string,
  ) {
    super(mensaje);
  }
}

export const ContextoApi = createContext<string>('');

/** Mensaje legible de una respuesta de error de los servicios (Nest o validación de zod). */
export function mensajeDeError(estado: number, cuerpo: unknown): string {
  const c = cuerpo as { message?: unknown; errores?: { mensaje?: string }[] } | null;
  if (Array.isArray(c?.errores) && c.errores[0]?.mensaje)
    return c.errores.map((e) => e.mensaje).join(' · ');
  if (typeof c?.message === 'string') return c.message;
  if (Array.isArray(c?.message)) return c.message.join(' · ');
  if (estado === 403) return 'Tu plan o tu rol no incluye esta función';
  if (estado === 404) return 'No encontrado';
  return `Error ${estado}`;
}

/** Llamadas al API Gateway con el token de la sesión: `api('GET', '/inventory/vehiculos')`. */
export function useApi() {
  const base = useContext(ContextoApi);
  const { token } = useSesion();
  return useCallback(
    async <T>(metodo: string, ruta: string, cuerpo?: unknown): Promise<T> => {
      const r = await fetch(`${base}/api${ruta}`, {
        method: metodo,
        headers: {
          authorization: `Bearer ${await token()}`,
          ...(cuerpo !== undefined && { 'content-type': 'application/json' }),
        },
        ...(cuerpo !== undefined && { body: JSON.stringify(cuerpo) }),
      }).catch((e: unknown) => {
        // 2.27: la llamada no llegó (sin red o Gateway caído); el frente lo reporta.
        window.dispatchEvent(
          new CustomEvent('plataforma:error-red', {
            detail: { ruta: `${metodo} ${ruta.split('?')[0]}`, mensaje: (e as Error).message },
          }),
        );
        throw new ErrorApi(0, 'No hay conexión con la plataforma; intenta de nuevo');
      });
      const texto = await r.text();
      const datos: unknown = texto ? JSON.parse(texto) : null;
      if (!r.ok) throw new ErrorApi(r.status, mensajeDeError(r.status, datos));
      if (metodo !== 'GET') avisarCambio();
      return datos as T;
    },
    [base, token],
  );
}

/** Sube un archivo tal cual (cuerpo binario con su tipo), p. ej. fotos y documentos del vehículo. */
export function useSubida() {
  const base = useContext(ContextoApi);
  const { token } = useSesion();
  return useCallback(
    async <T>(ruta: string, archivo: Blob): Promise<T> => {
      const r = await fetch(`${base}/api${ruta}`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${await token()}`,
          'content-type': archivo.type || 'application/octet-stream',
        },
        body: archivo,
      });
      const texto = await r.text();
      const datos: unknown = texto ? JSON.parse(texto) : null;
      if (!r.ok) throw new ErrorApi(r.status, mensajeDeError(r.status, datos));
      avisarCambio();
      return datos as T;
    },
    [base, token],
  );
}

/** Descarga un archivo protegido (con el token) y da una URL local para mostrarlo. */
export function useArchivo(ruta: string | null) {
  const base = useContext(ContextoApi);
  const { token } = useSesion();
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!ruta) return;
    let vivo = true;
    let local: string | null = null;
    void (async () => {
      const r = await fetch(`${base}/api${ruta}`, {
        headers: { authorization: `Bearer ${await token()}` },
      });
      if (!r.ok || !vivo) return;
      local = URL.createObjectURL(await r.blob());
      setUrl(local);
    })();
    return () => {
      vivo = false;
      if (local) URL.revokeObjectURL(local);
    };
  }, [base, token, ruta]);
  return url;
}

/** Consulta con estado de carga y error; `recargar()` la repite. */
export function useConsulta<T>(ruta: string | null) {
  const api = useApi();
  const [datos, setDatos] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [estadoError, setEstadoError] = useState<number | null>(null);
  const [vez, setVez] = useState(0);
  useEffect(() => {
    if (!ruta) return;
    let vivo = true;
    setError(null);
    setEstadoError(null);
    api<T>('GET', ruta)
      .then((d) => vivo && setDatos(d))
      .catch((e: Error) => {
        if (!vivo) return;
        setError(e.message);
        setEstadoError(e instanceof ErrorApi ? e.estado : null);
      });
    return () => {
      vivo = false;
    };
  }, [api, ruta, vez]);
  return {
    datos,
    error,
    estadoError,
    cargando: !datos && !error,
    recargar: () => setVez((v) => v + 1),
  };
}
